import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  freightAPI,
  shippersAPI,
  consigneesAPI,
  carriersAPI,
  driversAPI,
  trucksAPI,
} from '../../services/api';

function StopCard({ title, stop }) {
  if (!stop) return null;
  return (
    <div className="freight-import-stop-card">
      <div className="freight-import-stop-card__title">{title}</div>
      <div><strong>{stop.company_name || '—'}</strong></div>
      <div>{stop.address || '—'}</div>
      <div>{[stop.city, stop.state, stop.zip_code].filter(Boolean).join(', ') || '—'}</div>
      <div className="freight-import-meta">
        {stop.appointment_date ? `Date: ${stop.appointment_date}` : null}
        {stop.appointment_window ? ` · ${stop.appointment_window}` : null}
      </div>
      {stop.notes ? <div className="freight-import-notes">{stop.notes}</div> : null}
    </div>
  );
}

const FreightImport = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(null);

  const [shippers, setShippers] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);

  const [shipperId, setShipperId] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [truckId, setTruckId] = useState('');
  const [deliveryConsigneeIds, setDeliveryConsigneeIds] = useState([]);
  const [manifestTemplateId, setManifestTemplateId] = useState('');
  const [createManifests, setCreateManifests] = useState(true);
  const [createRoutePlan, setCreateRoutePlan] = useState(true);
  const [depotId, setDepotId] = useState('');
  const [freightNumber, setFreightNumber] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, cr, d, t] = await Promise.all([
          shippersAPI.getAll(),
          consigneesAPI.getAll(),
          carriersAPI.getAll(),
          driversAPI.getAll(),
          trucksAPI.getAll(),
        ]);
        setShippers(s);
        setConsignees(c);
        setCarriers(cr);
        setDrivers(d);
        setTrucks(t);
      } catch {
        /* optional */
      }
    })();
  }, []);

  const applySuggestions = (data) => {
    const sug = data.suggestions || {};
    if (sug.shipper_matches?.[0]) setShipperId(String(sug.shipper_matches[0].id));
    if (sug.carrier_matches?.[0]) setCarrierId(String(sug.carrier_matches[0].id));
    if (sug.driver_matches?.[0]) setDriverId(String(sug.driver_matches[0].id));
    if (sug.truck_matches?.[0]) setTruckId(String(sug.truck_matches[0].id));
    const dIds = (sug.delivery_matches || []).map((m) => (m[0] ? String(m[0].id) : ''));
    setDeliveryConsigneeIds(dIds);
    if (data.manifest_templates?.[0]) setManifestTemplateId(String(data.manifest_templates[0].id));
    const defaultDepot = (data.depots || []).find((x) => x.is_default) || data.depots?.[0];
    if (defaultDepot) setDepotId(String(defaultDepot.id));
    if (data.draft?.po_number) {
      const prefix = (data.draft.broker_name || data.draft.source || 'IMPORT')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 12)
        .toUpperCase();
      setFreightNumber(`${prefix}-${data.draft.po_number}`);
    }
  };

  const handleParse = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Choose a PDF rate confirmation.');
      return;
    }
    setError('');
    setParsing(true);
    try {
      const data = await freightAPI.importPdf(file);
      setPreview(data);
      setDraft(data.draft);
      applySuggestions(data);
      setStep('review');
    } catch (err) {
      setError(err.message || 'Failed to parse PDF');
    } finally {
      setParsing(false);
    }
  };

  const handleConfirm = async () => {
    if (!draft?.deliveries?.length) {
      setError('No delivery stops were parsed from this PDF. Re-upload or add stops manually in Freight.');
      return;
    }
    if (!shipperId) {
      setError('Select a shipper for the pickup stop.');
      return;
    }
    if (createRoutePlan && createManifests && !depotId) {
      setError('Select a depot for the route plan.');
      return;
    }
    setError('');
    setConfirming(true);
    try {
      const payload = {
        draft,
        shipper_id: parseInt(shipperId, 10),
        carrier_id: carrierId ? parseInt(carrierId, 10) : null,
        driver_id: driverId ? parseInt(driverId, 10) : null,
        truck_id: truckId ? parseInt(truckId, 10) : null,
        delivery_consignee_ids: deliveryConsigneeIds.map((id) => parseInt(id, 10)),
        manifest_template_id: manifestTemplateId ? parseInt(manifestTemplateId, 10) : null,
        create_manifests: createManifests,
        create_route_plan: createRoutePlan && createManifests,
        depot_id: depotId ? parseInt(depotId, 10) : null,
        freight_number: freightNumber.trim(),
      };
      const res = await freightAPI.confirmImport(payload);
      setResult(res);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Import failed');
    } finally {
      setConfirming(false);
    }
  };

  const deliveries = draft?.deliveries || [];

  return (
    <div className="management-page freight-import-page">
      <div className="page-header">
        <h1>Import rate confirmation (PDF)</h1>
        <Link to="/freight" className="btn-secondary">← Back to Freight</Link>
      </div>

      <p className="freight-import-intro">
        Upload a broker rate confirmation PDF (TQL, GlobalTranz, BM2, Freight Tec, ShipSilo, Scout Freight, Steamboat). Scanned PDFs are OCR’d automatically. Review parsed pickup and delivery stops,
        match shipper/consignees, then create freight, manifests, and an optional route plan draft.
      </p>

      {error ? <div className="freight-import-error">{error}</div> : null}

      {step === 'upload' ? (
        <form onSubmit={handleParse} className="freight-import-upload">
          <div className="form-group">
            <label>PDF file</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={parsing}>
            {parsing ? 'Parsing…' : 'Upload & parse'}
          </button>
        </form>
      ) : null}

      {step === 'review' && draft ? (
        <div className="freight-import-review">
          <div className="freight-import-grid">
            <section className="freight-import-panel">
              <h2>Parsed load</h2>
              <div className="freight-import-kv">
                <span>PO#</span><strong>{draft.po_number || '—'}</strong>
                <span>Pickup</span><strong>{draft.pickup_date || '—'}</strong>
                <span>Delivery</span><strong>{draft.delivery_date || '—'}</strong>
                <span>Weight</span><strong>{draft.weight_lbs ? `${draft.weight_lbs} lb` : '—'}</strong>
                <span>Equipment</span><strong>{draft.trailer_size_ft ? `${draft.trailer_size_ft} ft box` : '—'}</strong>
                <span>Commodity</span><strong>{draft.commodity || '—'}</strong>
              </div>
              <StopCard title="Pickup" stop={draft.pickup} />
              {deliveries.length === 0 ? (
                <div className="freight-import-error" style={{ marginTop: '0.75rem' }}>
                  No delivery stops detected in this PDF. The file may use a layout we do not support yet — check the
                  extracted fields or enter freight manually.
                </div>
              ) : (
                deliveries.map((d, i) => (
                  <StopCard key={i} title={`Delivery ${i + 1}`} stop={d} />
                ))
              )}
              {draft.special_instructions ? (
                <div className="freight-import-notes-block">
                  <strong>Special instructions</strong>
                  <p>{draft.special_instructions}</p>
                </div>
              ) : null}
            </section>

            <section className="freight-import-panel">
              <h2>Match & assign</h2>
              <div className="form-group">
                <label>Freight number *</label>
                <input value={freightNumber} onChange={(e) => setFreightNumber(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Shipper (pickup) *</label>
                <select value={shipperId} onChange={(e) => setShipperId(e.target.value)} required>
                  <option value="">Select shipper</option>
                  {shippers.map((s) => (
                    <option key={s.id} value={s.id}>{s.company_name}</option>
                  ))}
                </select>
              </div>
              {deliveries.map((d, i) => (
                <div className="form-group" key={i}>
                  <label>Consignee — {d.company_name || `Stop ${i + 1}`} *</label>
                  <select
                    value={deliveryConsigneeIds[i] || ''}
                    onChange={(e) => {
                      const next = [...deliveryConsigneeIds];
                      next[i] = e.target.value;
                      setDeliveryConsigneeIds(next);
                    }}
                    required
                  >
                    <option value="">Select consignee</option>
                    {consignees.map((c) => (
                      <option key={c.id} value={c.id}>{c.company_name} ({c.city}, {c.state})</option>
                    ))}
                  </select>
                </div>
              ))}
              <div className="form-row">
                <div className="form-group">
                  <label>Carrier</label>
                  <select value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>
                    <option value="">—</option>
                    {carriers.map((c) => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Driver</label>
                  <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
                    <option value="">—</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Truck</label>
                <select value={truckId} onChange={(e) => setTruckId(e.target.value)}>
                  <option value="">—</option>
                  {trucks.map((t) => (
                    <option key={t.id} value={t.id}>{t.truck_number}{t.length_feet ? ` (${t.length_feet} ft)` : ''}</option>
                  ))}
                </select>
              </div>

              <h3>Manifests & route</h3>
              <label className="freight-import-check">
                <input type="checkbox" checked={createManifests} onChange={(e) => setCreateManifests(e.target.checked)} />
                Create one manifest per delivery stop
              </label>
              {createManifests ? (
                <div className="form-group">
                  <label>Manifest template</label>
                  <select value={manifestTemplateId} onChange={(e) => setManifestTemplateId(e.target.value)}>
                    <option value="">Default (no template)</option>
                    {(preview?.manifest_templates || []).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              ) : null}
              <label className="freight-import-check">
                <input
                  type="checkbox"
                  checked={createRoutePlan}
                  disabled={!createManifests}
                  onChange={(e) => setCreateRoutePlan(e.target.checked)}
                />
                Create route plan draft and add manifests as stops
              </label>
              {createRoutePlan && createManifests ? (
                <div className="form-group">
                  <label>Depot</label>
                  <select value={depotId} onChange={(e) => setDepotId(e.target.value)}>
                    <option value="">Select depot</option>
                    {(preview?.depots || []).map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setStep('upload')}>Back</button>
                <button type="button" className="btn-primary" onClick={handleConfirm} disabled={confirming}>
                  {confirming ? 'Creating…' : 'Create freight & stops'}
                </button>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {step === 'done' && result ? (
        <div className="freight-import-done">
          <h2>Import complete</h2>
          <p>Freight <strong>{result.freight_number}</strong> created (ID {result.freight_id}).</p>
          {result.manifest_ids?.length ? (
            <p>{result.manifest_ids.length} manifest(s) created.</p>
          ) : null}
          {result.route_plan?.plan?.id ? (
            <p>
              Route plan draft created —{' '}
              <Link to={`/route-planner/${result.route_plan.plan.id}`}>Open route planner</Link>
              {' '}
              ({result.route_plan.plan.stops?.length ?? 0} stop
              {(result.route_plan.plan.stops?.length ?? 0) === 1 ? '' : 's'} on plan)
            </p>
          ) : null}
          {result.route_plan?.stops_added?.length ? (
            <ul className="freight-import-stop-summary">
              {result.route_plan.stops_added.map((s) => (
                <li key={`${s.stop_type}-${s.sequence}`}>
                  <strong>{s.stop_type === 'pickup' ? 'Pickup' : 'Delivery'}:</strong>{' '}
                  {s.label} — {s.address_text}
                </li>
              ))}
            </ul>
          ) : null}
          {result.route_plan?.pickup_added === false ? (
            <p className="freight-import-warning">
              Pickup was not added to the route plan
              {result.route_plan.pickup_skipped_reason
                ? `: ${result.route_plan.pickup_skipped_reason}`
                : ''}
              .
            </p>
          ) : null}
          {result.route_plan?.stops_skipped?.length ? (
            <p className="freight-import-warning">
              {result.route_plan.stops_skipped.length} stop(s) were not added to the route plan:
              <ul className="freight-import-stop-summary">
                {result.route_plan.stops_skipped.map((s) => (
                  <li key={`skip-${s.stop_type}-${s.sequence}`}>
                    {s.stop_type} — {s.company_name || s.city || 'stop'} ({s.reason})
                  </li>
                ))}
              </ul>
            </p>
          ) : null}
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={() => navigate('/freight')}>
              Go to Freight list
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FreightImport;
